import React, { Fragment, useEffect, useState } from "react";
import { Box, Button, Card, Divider, Grid2 as Grid, Typography } from "@mui/material";
import { GenericMuiDatagrid } from "../../../components/generic/GenericDatagrid";
import { formatDate } from "../../../helpers/dateHelper";
import { getBroadcastMessages, getUserDetails } from "../../../config/apisauce";
import { GENERIC_COLUMN_PROPS, INITIAL_GET_PARAMS } from "../../../enums/generalEnums";
import { DAY_ONLY_FORMAT } from "../../../enums/dateEnums";
import { BroadcastMessageDetailsDialog } from "../dialogs/BroadcastMessageDetailsDialog";
import { GenericNoData } from "../../../components/generic/GenericNoData";
import { Megaphone, Plus } from "@phosphor-icons/react";
import useStore from "../../../states/store";
import { NewBroadcastMessageForm } from "./NewBroadcastMessageForm";
import { usePage } from "@inertiajs/react";
import { htmlToText } from "../../../helpers/stringHelper";

const NotApplicable = () => {
  return (
    <Typography mt={1.7} color="grey.light">N/A</Typography>
  );
};

export const BroadcastMessages = () => {
  const [, { openLoader, closeLoader }] = useStore();
  const [broadcastMessages, setbroadcastMessages] = useState({});
  const [openBroadcastMessageDialog, setOpenBroadcastMessageDialog] = useState(false);
  const [openNewBroadcastMessageForm, setOpenNewBroadcastMessageForm] = useState(false);
  const [dialogDetails, setDialogDetails] = useState({});
  const [details, setDetails] = useState('');
  const [pagination, setPagination] = useState({ pageSize: 5, page: 0 });
  const { auth } = usePage().props;
  const [creatorDetails, setCreatorDetails] = useState({});

  const handleOpenDialog = (data) => {
    setDialogDetails({ data: data });
    setDetails(data);
    setOpenBroadcastMessageDialog(true);
  };

  const handleOpenNewBroadcastMessageForm = () => {
    setOpenNewBroadcastMessageForm(true);
  };

  const handleCloseBroadcastMessageDialog = () => {
    setOpenBroadcastMessageDialog(false);
    setDialogDetails({});
  };

  const handleCloseNewBroadcastMessageForm = () => {
    setOpenNewBroadcastMessageForm(false);
  };

  const fetchCreatorDetails = async () => {
    openLoader();
    const { ok, data } = await getUserDetails({ user_id: auth?.user?.id });
    if (ok) {
      setCreatorDetails(data);
    }
    closeLoader();
  };

  const fetchBroadcastMessages = async () => {
    const params = {
      ...INITIAL_GET_PARAMS,
      per_page: pagination?.pageSize,
      page: pagination?.page + 1
    };
    openLoader();
    const response = await getBroadcastMessages(params);
    if (response.ok) {
      setbroadcastMessages(response?.data);
    }
    closeLoader();
  };

  useEffect(() => {
    fetchCreatorDetails();
  }, []);

  useEffect(() => {
    fetchBroadcastMessages();
  }, [pagination]);

  const columns = [
    {
      field: 'created_at',
      headerName: 'Date',
      flex: 1,
      renderCell: ({ row }) => formatDate(new Date(row?.created_at)),
      ...GENERIC_COLUMN_PROPS
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      renderCell: ({ row }) => row?.title ?? <NotApplicable />,
      ...GENERIC_COLUMN_PROPS
    },
    {
      field: 'message',
      headerName: 'Message',
      flex: 1,
      renderCell: ({ row }) => htmlToText(row?.message) ?? <NotApplicable />,
      ...GENERIC_COLUMN_PROPS
    },
    {
      field: 'created_by',
      headerName: 'Created By',
      flex: 1,
      renderCell: ({ row }) => row?.creator?.name ?? <NotApplicable />,
      ...GENERIC_COLUMN_PROPS
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
      renderCell: ({ row }) => row?.type ?? <NotApplicable />,
      ...GENERIC_COLUMN_PROPS
    },
    {
      field: 'recipient',
      headerName: 'Recipient',
      flex: 1,
      renderCell: ({ row }) => `${row?.recipient_count} creator/s`,
      ...GENERIC_COLUMN_PROPS
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1.4,
      renderCell: ({ row }) => {
        return (
          <Button onClick={() => handleOpenDialog(row)} fullWidth variant="contained" sx={{ color: '#FFF' }}>Details</Button>
        );
      },
      ...GENERIC_COLUMN_PROPS
    }
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflowY: 'auto' }}>
      <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', padding: 2 }}>
        <Grid container spacing={1.5}>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Typography variant="h6" fontWeight={700} color="primary">
              Broadcast Messages
            </Typography>
          </Grid>
          <Grid container item justifyContent={'flex-end'} size={{ xs: 12, sm: 6 }}>
            <Button sx={{ width: 150 }} startIcon={<Plus size={16} weight="bold" />} onClick={() => handleOpenNewBroadcastMessageForm()}>New Broadcast</Button>
          </Grid>
        </Grid>

        <Typography fontWeight={500} mb={2}>View and manage all broadcast messages.</Typography>

        {
          broadcastMessages?.data?.length > 0 ? <GenericMuiDatagrid
            {...broadcastMessages}
            columns={columns}
            setPagination={setPagination}
          >
            {
              broadcastMessages?.data?.map((message, index) => {
                return (
                  <Fragment key={`broadcast-message-key-${index}`}>
                    <Box sx={{ padding: '16px' }}>
                      <Grid container>
                        <Grid container item justifyContent={'flex-end'} size={2}>
                          <Typography color="text.light">{formatDate(new Date(message?.created_at), DAY_ONLY_FORMAT)}</Typography>
                        </Grid>
                        <Grid container item justifyContent={'flex-end'} size={2}>
                          <Typography color="text.light">{message?.title}</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    <Divider sx={{ borderColor: 'borders.main' }} />
                  </Fragment>
                );
              })
            }
          </GenericMuiDatagrid> : <GenericNoData icon={<Megaphone size={40} />} height={'67vh'} label={'No Broadcast Messages yet'} />
        }
      </Card>
      <NewBroadcastMessageForm
        details={details}
        setDetails={setDetails}
        creatorDetails={creatorDetails}
        openDialog={openNewBroadcastMessageForm}
        handleCloseDialog={() => handleCloseNewBroadcastMessageForm()}
        onMessageCreated={fetchBroadcastMessages}
      />
      <BroadcastMessageDetailsDialog
        details={details}
        setDetails={setDetails}
        dialogDetails={dialogDetails}
        openDialog={openBroadcastMessageDialog}
        handleCloseDialog={() => handleCloseBroadcastMessageDialog()}
      />
    </Box >
  );
};